import { useEffect, useMemo, useState } from "react";
import { DatePicker } from "antd";
import { Table } from "antd";
import axios from "axios";
import dayjs from "dayjs";
import { CalendarOutlined, WalletOutlined } from "@ant-design/icons";
import PdfExcelPrint from "../Utilities/PdfExcelPrint";
import styles from "../shared/Inventory.module.css";
import InventoryPageHeader from "../shared/InventoryPageHeader";
import InventoryTable from "../shared/InventoryTable";
import { CREATE_jwel } from "../Config/Config";
import Header from "../Header";
import SidebarDrawer from "../SidebarDrawer";

const { RangePicker } = DatePicker;

/* ─── filter box theme palette (same as OutstandingCustomers / URDPurchaseDetailsRegister) ─── */
const FILTER_THEMES = {
  blue: {
    text: "#1d4ed8",
    bg: "#ffffff",
    border: "#93c5fd",
    ring: "rgba(29,78,216,0.15)",
  },
  amber: {
    text: "#b45309",
    bg: "#ffffff",
    border: "#fcd34d",
    ring: "rgba(245,158,11,0.15)",
  },
};

const FilterField = ({ icon, label, theme = "blue", width, children }) => {
  const c = FILTER_THEMES[theme];
  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: 6,
        background: c.bg,
        border: `2px solid ${c.border}`,
        borderRadius: 12,
        padding: "7px 10px 9px",
        minHeight: 64,
        width,
        boxShadow: `0 1px 3px ${c.ring}`,
        transition: "box-shadow 0.2s ease, transform 0.2s ease",
      }}
    >
      <span
        style={{
          display: "flex",
          alignItems: "center",
          gap: 6,
          fontSize: 12,
          fontWeight: 700,
          color: c.text,
          textTransform: "uppercase",
          letterSpacing: 0.4,
        }}
      >
        {icon} {label}
      </span>
      <div
        style={{
          background: "#fff",
          border: "1.5px solid #d6dbe3",
          borderRadius: 8,
          padding: "1px 8px",
          display: "flex",
          alignItems: "center",
        }}
      >
        {children}
      </div>
    </div>
  );
};

/* ─── small colored amount chips (Receipt/Payment), same visual family
   as the wtBadge/tagBadge chips used on the other redesigned screens ─── */
const amountChipStyle = (kind) => {
  const palette = {
    receipt: { bg: "#dcfce7", color: "#15803d", border: "#86efac" },
    payment: { bg: "#fee2e2", color: "#b91c1c", border: "#fca5a5" },
  }[kind];

  return {
    display: "inline-block",
    padding: "3px 10px",
    borderRadius: 20,
    fontSize: 12.5,
    fontWeight: 700,
    background: palette.bg,
    color: palette.color,
    border: `1px solid ${palette.border}`,
  };
};

/* ---------------- Shared card-view field maps (mobile) ---------------- */
const primaryFields = [
  { key: "VNO", label: "Voucher No" },
  { key: "PARTICULARS", label: "Particulars" },
];
const secondaryFields = [
  { key: "JAMAAMOUNT", label: "Receipt" },
  { key: "NAMAAMOUNT", label: "Payment" },
  { key: "BALANCE", label: "Balance" },
];

const CashBook = () => {
  const [data, setData] = useState([]);
  const [openingCash, setOpeningCash] = useState(0);
  const [dates, setDates] = useState([dayjs(), dayjs()]);
  const [loading, setLoading] = useState(false);
  const [tableVisible, setTableVisible] = useState(false);
  const tenantName = localStorage.getItem("tenantName");
  const [open, setOpen] = useState(false);
  const userArea = localStorage.getItem("city");
  const userName = localStorage.getItem("user");
  const singleImage = localStorage.getItem("singleImage");

  const toggleDrawer = () => {
    setOpen(false);
  };

  /* ---- data / API logic (unchanged endpoints, same query params/formats) ---- */

  useEffect(() => {
    if (!dates[0] || !dates[1]) return;

    let isMounted = true;
    setLoading(true);
    setTableVisible(false);

    const fromDateOpening = dayjs(dates[0]).format("MM/DD/YYYY");
    const fromDateData = dayjs(dates[0]).format("YYYY-MM-DD");
    const toDateData = dayjs(dates[1]).format("YYYY-MM-DD");

    axios
      .get(
        `${CREATE_jwel}/api/ERP/GetCashBookOpening?fromDate=${fromDateOpening}`,
        {
          headers: {
            tenantName: tenantName,
          },
        },
      )
      .then((openingResponse) => {
        if (!isMounted) return;

        const openingData = openingResponse.data;
        let openingVal = 0;

        if (Array.isArray(openingData) && openingData.length > 0) {
          const jama = Number(openingData[0].JAMAAMOUNT || 0);
          const nama = Number(openingData[0].NAMAAMOUNT || 0);
          openingVal = jama - nama;
        }

        setOpeningCash(openingVal);

        return axios
          .get(
            `${CREATE_jwel}/api/ERP/GetCashBookData?fromDate=${fromDateData}&toDate=${toDateData}`,
            {
              headers: {
                tenantName: tenantName,
              },
            },
          )
          .then((detailsResponse) => {
            if (!isMounted) return;

            const detailsData = Array.isArray(detailsResponse.data)
              ? detailsResponse.data
              : [];
            let balance = openingVal;

            const calculatedData = detailsData.map((item, index) => {
              const jama = Number(item.JAMAAMOUNT || 0);
              const nama = Number(item.NAMAAMOUNT || 0);
              balance = balance + jama - nama;
              return {
                ...item,
                key: index + 1,
                BALANCE: balance,
                serialNo: index + 1,
              };
            });

            setData(calculatedData);
            setTimeout(() => setTableVisible(true), 80);
          });
      })
      .catch((error) => {
        if (isMounted) {
          console.error("Error fetching cash book data:", error);
        }
      })
      .finally(() => {
        if (isMounted) {
          setLoading(false);
        }
      });

    return () => {
      isMounted = false;
    };
  }, [dates, tenantName]);

  const columns = [
    {
      title: "S.No",
      dataIndex: "serialNo",
      width: 50,
      align: "center",
      key: "serialNo",
      className: "col-sno",
    },
    {
      title: "Voucher No",
      dataIndex: "VNO",
      key: "VNO",
      render: (value) => <span className="tagBadge">{value}</span>,
    },
    {
      title: "Entry Date",
      dataIndex: "ENTRYDATE",
      key: "ENTRYDATE",
      align: "center",
      render: (date) => (date ? dayjs(date).format("DD MMM YYYY") : "-"),
    },
    { title: "Particulars", dataIndex: "PARTICULARS", key: "PARTICULARS" },
    {
      title: "Receipt",
      dataIndex: "JAMAAMOUNT",
      key: "JAMAAMOUNT",
      align: "right",
      render: (value) => (
        <span style={amountChipStyle("receipt")}>
          {Number(value || 0).toFixed(2)}
        </span>
      ),
    },
    {
      title: "Payment",
      dataIndex: "NAMAAMOUNT",
      key: "NAMAAMOUNT",
      align: "right",
      render: (value) => (
        <span style={amountChipStyle("payment")}>
          {Number(value || 0).toFixed(2)}
        </span>
      ),
    },
    {
      title: "Balance",
      dataIndex: "BALANCE",
      key: "BALANCE",
      align: "right",
      render: (value) => (
        <strong style={{ color: Number(value) < 0 ? "#ef4444" : "#0f766e" }}>
          {Number(value || 0).toFixed(2)}
        </strong>
      ),
    },
  ];

  const totals = useMemo(() => {
    return data.reduce(
      (acc, item) => {
        acc.debit += Number(item.NAMAAMOUNT || 0);
        acc.credit += Number(item.JAMAAMOUNT || 0);
        return acc;
      },
      { debit: 0, credit: 0 },
    );
  }, [data]);

  const closingBalance = useMemo(() => {
    return data.length > 0 ? data[data.length - 1].BALANCE : openingCash;
  }, [data, openingCash]);

  const formattedData = useMemo(
    () =>
      data.map((item, index) => ({
        ...item,
        serialNo: index + 1,
        NAMAAMOUNT: Number(item.NAMAAMOUNT || 0),
        JAMAAMOUNT: Number(item.JAMAAMOUNT || 0),
        BALANCE: Number(item.BALANCE || 0),
        ENTRYDATE: item?.ENTRYDATE
          ? dayjs(item.ENTRYDATE).format("DD/MMM/YYYY")
          : "",
        VDATE: item?.VDATE ? dayjs(item.VDATE).format("DD/MMM/YYYY") : "",
      })),
    [data],
  );

  const exportData = [
    ...formattedData,
    {
      serialNo: "Total",
      VNO: "",
      ENTRYDATE: "",
      PARTICULARS: "",
      NAMAAMOUNT: totals.debit.toFixed(2),
      JAMAAMOUNT: totals.credit.toFixed(2),
      BALANCE: closingBalance.toFixed(2),
    },
  ];

  return (
    <div className={styles.page}>
      <Header setOpen={setOpen} />
      <style>{`
        .ant-table-thead > tr > th {
          white-space: nowrap !important;
        }
      `}</style>

      <InventoryPageHeader
        title="Cash Book"
        subtitle="Accounts · Cash Book"
        icon={<CalendarOutlined style={{ color: "#fff", fontSize: 16 }} />}
        recordCount={data.length}
        visible={data.length > 0}
        stats={[
          { label: "Records", value: data.length, color: "blue" },
          {
            label: "Opening Balance",
            value: openingCash.toFixed(2),
            color: "amber",
          },
          {
            label: "Receipt",
            value: totals.credit.toFixed(2),
            color: "green",
          },
          {
            label: "Payment",
            value: totals.debit.toFixed(2),
            color: "red",
          },
          {
            label: "Closing Balance",
            value: closingBalance.toFixed(2),
            color: "teal",
          },
        ]}
      />

      <div
        className={styles.toolbar}
        style={{ flexWrap: "wrap", gap: 10, alignItems: "stretch" }}
      >
        <div
          className={styles.toolbarLeft}
          style={{ flexWrap: "wrap", gap: 10, alignItems: "stretch" }}
        >
          <FilterField
            icon={<CalendarOutlined />}
            label="Date Range"
            theme="blue"
          >
            <RangePicker
              value={dates}
              onChange={(range) => {
                if (range?.[0] && range?.[1]) setDates(range);
              }}
              format="DD/MM/YYYY"
              size="middle"
              bordered={false}
              allowClear={false}
              style={{ width: 240 }}
              disabledDate={(current) =>
                current && current.isAfter(dayjs(), "day")
              }
            />
          </FilterField>

          <FilterField
            icon={<WalletOutlined />}
            label="Opening Balance"
            theme="amber"
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 6,
                padding: "2px 4px",
                height: 28,
              }}
            >
              <span
                style={{
                  fontSize: 11,
                  fontWeight: 700,
                  color: "#92400e",
                  letterSpacing: 0.3,
                }}
              >
                OPENING BAL:
              </span>
              <strong
                style={{
                  fontSize: 13.5,
                  fontWeight: 800,
                  color: openingCash < 0 ? "#dc2626" : "#b45309",
                }}
              >
                {openingCash.toFixed(2)}
              </strong>
            </div>
          </FilterField>
        </div>

        <div className={styles.toolbarRight}>
          <PdfExcelPrint
            data={exportData}
            columns={columns}
            fileName="CashBook"
            totals={{
              totalDebit: totals.debit.toFixed(2),
              totalCredit: totals.credit.toFixed(2),
            }}
            disabled={formattedData.length === 0}
          />
        </div>
      </div>

      <InventoryTable
        columns={columns}
        dataSource={formattedData}
        rowKey="serialNo"
        tableClassName="mr-table"
        tableEnter={tableVisible}
        loading={loading}
        emptyText="No cash book entries found for the selected dates"
        primaryFields={primaryFields}
        secondaryFields={secondaryFields}
        summary={() => (
          <Table.Summary.Row style={{ background: "#f5f5f5", fontWeight: 600 }}>
            <Table.Summary.Cell>Total</Table.Summary.Cell>
            <Table.Summary.Cell />
            <Table.Summary.Cell />
            <Table.Summary.Cell />
            <Table.Summary.Cell align="right">
              {totals.credit.toFixed(2)}
            </Table.Summary.Cell>
            <Table.Summary.Cell align="right">
              {totals.debit.toFixed(2)}
            </Table.Summary.Cell>
            <Table.Summary.Cell align="right">
              {closingBalance.toFixed(2)}
            </Table.Summary.Cell>
          </Table.Summary.Row>
        )}
      />
      <SidebarDrawer
        open={open}
        toggleDrawer={toggleDrawer}
        singleImage={singleImage}
        userArea={userArea}
        userName={userName}
      />
    </div>
  );
};

export default CashBook;
